/**
 * src/components/Financial/modules/HelionTransitModule.jsx
 * 【完全修正版：ダークモード対応・デザイン統一・フチ取り削除】
 */
import React from "react";
import { Sun } from "lucide-react";
import { PLANET_SYMBOLS, PLANET_COLORS } from "../../../utils/constants.js";
import * as Astro from "../../../astronomy.js";

/**
 * 近日点・遠日点 計算ロジック
 * timeline（fullTimeline）をスキャンして距離の極値を判定します。
 */
export const calculateHelionTransits = function (timeline, selectedPlanets) {
  var hits = [];
  if (
    !timeline ||
    timeline.length < 3 ||
    !selectedPlanets ||
    selectedPlanets.size === 0
  )
    return hits;

  // 日付取得の統一（未来の空バーにも対応）
  var getDate = function (row) {
    return row.timestamp ? new Date(row.timestamp) : new Date(row.Date);
  };

  selectedPlanets.forEach(function (p) {
    for (var i = 1; i < timeline.length - 1; i++) {
      var d0 = getPlanetDistance(p, getDate(timeline[i - 1]));
      var d1 = getPlanetDistance(p, getDate(timeline[i]));
      var d2 = getPlanetDistance(p, getDate(timeline[i + 1]));

      if (d0 === 0 || d1 === 0 || d2 === 0) continue;

      var slope1 = d1 - d0;
      var slope2 = d2 - d1;

      // 近日点 (Perihelion): 距離が減少から増加に転じる瞬間
      if (slope1 < 0 && slope2 > 0) {
        hits.push({
          index: i,
          planet: p,
          type: "Perihelion",
          symbol: "P",
          date:
            timeline[i].Date ||
            new Date(timeline[i].timestamp).toISOString().split("T")[0],
        });
      }
      // 遠日点 (Aphelion): 距離が増加から減少に転じる瞬間
      else if (slope1 > 0 && slope2 < 0) {
        hits.push({
          index: i,
          planet: p,
          type: "Aphelion",
          symbol: "A",
          date:
            timeline[i].Date ||
            new Date(timeline[i].timestamp).toISOString().split("T")[0],
        });
      }
    }
  });
  return hits;
};

/**
 * 惑星の日心距離(AU)を取得するヘルパー
 */
function getPlanetDistance(planet, date) {
  try {
    var time = Astro.MakeTime ? Astro.MakeTime(date) : date;
    if (Astro.HelioVector) {
      var vec = Astro.HelioVector(planet, time);
      return Math.sqrt(vec.x * vec.x + vec.y * vec.y + vec.z * vec.z);
    }
    return 0;
  } catch (e) {
    return 0;
  }
}

/**
 * 描画レイヤー (ダークモード対応・フチ取り削除版)
 */
export const HelionTransitLayer = function (props) {
  var hits = props.hits || [];
  var coords = props.coords;
  var displayRange = props.displayRange;
  var planetColors = props.planetColors || {};
  var isDarkMode = props.isDarkMode;

  return (
    <g className="helion-transit-layer">
      {hits.map(function (hit, i) {
        if (
          hit.index < displayRange.start ||
          hit.index >= displayRange.start + displayRange.count
        )
          return null;
        var xPos = coords.getX(hit.index - displayRange.start);
        var pColor =
          planetColors[hit.planet] || PLANET_COLORS[hit.planet] || "#64748b";

        return (
          <g key={"helion-" + i}>
            {/* 垂直点線グリッド */}
            <line
              x1={xPos}
              y1={coords.pad.t}
              x2={xPos}
              y2={coords.pad.t + coords.iH}
              stroke={pColor}
              strokeDasharray="4 4"
              opacity="0.6"
            />
            {/* 惑星アイコンと P/A ラベル（縁取りなし） */}
            <g
              transform={"translate(" + xPos + ", " + (coords.pad.t - 30) + ")"}
            >
              <text
                x="0"
                y="0"
                fill={pColor}
                fontSize="14"
                textAnchor="middle"
                fontWeight="bold"
              >
                {PLANET_SYMBOLS[hit.planet]}
              </text>
              <text
                x="0"
                y="12"
                fill={pColor}
                fontSize="9"
                textAnchor="middle"
                fontWeight="bold"
              >
                {hit.symbol}
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
};

/**
 * 設定UI (ダークモード対応・デザイン統一版)
 */
export const HelionTransitSettingsUI = function (props) {
  var show = props.show;
  var setShow = props.setShow;
  var selectedPlanets = props.selectedPlanets;
  var setSelectedPlanets = props.setSelectedPlanets;
  var isDarkMode = props.isDarkMode;

  var planets = [
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];

  // テーマ定義の統一
  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
  };

  var togglePlanet = function (p) {
    var next = new Set(selectedPlanets);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setSelectedPlanets(next);
  };

  /**
   * ボタン共通スタイル (f98.jpg に合わせた統一デザイン)
   */
  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  /**
   * 天体選択ボタンの共通スタイル
   */
  var plBtnSt = function (p, active) {
    var pColor = PLANET_COLORS[p] || "#6366f1";
    return {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 14px",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: "600",
      cursor: "pointer",
      background: active
        ? isDarkMode
          ? "#2c2d96"
          : "#ffffff"
        : isDarkMode
          ? "#1c1f26"
          : "#f1f5f9",
      border:
        "1.5px solid " + (active ? pColor : isDarkMode ? "#2d313d" : "#e2e8f0"),
      color: active
        ? isDarkMode
          ? "#ffffff"
          : pColor
        : isDarkMode
          ? "#9ca3af"
          : "#64748b",
    };
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            color: theme.text,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Sun size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />{" "}
          <span>近日点・遠日点 (Helion Transits)</span>
        </div>

        <button
          onClick={function () {
            setShow(!show);
          }}
          style={btnSt(show)}
        >
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          marginBottom: "15px",
          fontSize: "12px",
          color: theme.subText,
          lineHeight: "1.6",
        }}
      >
        <div style={{ fontWeight: "bold" }}>
          このツールは、ヘリオセントリック(太陽中心)モードに切り替えて使用。
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        {planets.map(function (p) {
          var active = selectedPlanets.has(p);
          return (
            <button
              key={p}
              onClick={function () {
                togglePlanet(p);
              }}
              style={plBtnSt(p, active)}
            >
              <span style={{ fontSize: "14px" }}>{PLANET_SYMBOLS[p]}</span> {p}
            </button>
          );
        })}
      </div>
    </div>
  );
};
